import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import type { Market } from '../dataProvider/types';
import { TransitionButton } from '../actions/TransitionButton';
import type {
  ReferralsBand as Band,
  ReferrerGroup,
  RefereeRow,
} from './referralsModel';
import { REWARD_COLOR, REWARD_LABEL, formatProgress } from './referralState';

/**
 * One full-width market band, keyed by REFERRER. Each referrer is an expandable
 * group (defaultExpanded) summarising its referee count and reward mix, with the
 * individual referees beneath. Banded by the referrer's market; a cross-market
 * referee is flagged, never hidden. Single market per band, no cross-market total.
 *
 * The one guarded write is Void (referrals:void, editor), on a referral that is
 * not already paid or void, through the existing performStatusTransition +
 * withAudit chokepoint. There is no admin reward-paying action (paying is the
 * engine's).
 */

const DAY = 86_400_000;

const MARKET_NAME: Record<Market, string> = {
  uk: 'United Kingdom',
  us: 'United States',
  ie: 'Ireland',
};

const RefereeTableRow = ({
  row,
  now,
  canVoid,
}: {
  row: RefereeRow;
  now: number;
  canVoid: boolean;
}) => {
  const referredDays =
    row.referredAt != null
      ? Math.max(0, Math.floor((now - Date.parse(row.referredAt)) / DAY))
      : null;
  return (
    <TableRow data-testid={`ref-referee-${row.id}`}>
      <TableCell>
        <Typography variant="body2">{row.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.email}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
          <span>{row.market.toUpperCase()}</span>
          {row.crossMarket && (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label="cross-market"
            />
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          variant="outlined"
          color={REWARD_COLOR[row.rewardStatus]}
          label={REWARD_LABEL[row.rewardStatus]}
        />
      </TableCell>
      <TableCell>
        {/* Spend progress is shown only on a pending referral. */}
        {row.rewardStatus === 'pending' ? formatProgress(row) : '—'}
      </TableCell>
      <TableCell>
        {referredDays == null ? '—' : `${referredDays} days ago`}
      </TableCell>
      <TableCell align="right">
        {/* Void only on a not-yet-paid, not-yet-void referral, for a voider. */}
        {canVoid && row.voidable && (
          <TransitionButton
            resource="users"
            capability="referrals:void"
            id={row.id}
            data={{ referralRewardStatus: 'void' }}
            previousData={{
              id: row.id,
              referralRewardStatus: row.rewardStatus,
            }}
            label="Void"
            color="error"
            confirmTitle="Void referral"
            confirmContent="Void this referral? This stops any reward and cannot be undone. Use it for fraud or abuse."
            successMessage="Referral voided"
          />
        )}
      </TableCell>
    </TableRow>
  );
};

const ReferrerAccordion = ({
  group,
  now,
  canVoid,
}: {
  group: ReferrerGroup;
  now: number;
  canVoid: boolean;
}) => (
  <Accordion defaultExpanded data-testid={`ref-referrer-${group.referrerId}`}>
    <AccordionSummary expandIcon={<span aria-hidden>v</span>}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {group.referrerName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {group.referrerEmail}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={`${group.refereeCount} referees`}
          data-testid={`ref-referrer-${group.referrerId}-count`}
        />
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          {group.counts.qualified > 0 && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`${group.counts.qualified} qualified`}
            />
          )}
          {group.counts.paid > 0 && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={`${group.counts.paid} paid`}
            />
          )}
          {group.counts.pending > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={`${group.counts.pending} pending`}
            />
          )}
          {group.counts.void > 0 && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={`${group.counts.void} void`}
            />
          )}
        </Stack>
      </Stack>
    </AccordionSummary>
    <AccordionDetails>
      <Table size="small" aria-label={`referees of ${group.referrerName}`}>
        <TableHead>
          <TableRow>
            <TableCell>Referee</TableCell>
            <TableCell>Market</TableCell>
            <TableCell>Reward</TableCell>
            <TableCell>Spend progress</TableCell>
            <TableCell>Referred</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {group.referees.map((r) => (
            <RefereeTableRow key={r.id} row={r} now={now} canVoid={canVoid} />
          ))}
        </TableBody>
      </Table>
    </AccordionDetails>
  </Accordion>
);

export const ReferralsBand = ({ band, now }: { band: Band; now: number }) => {
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const canVoid = hasAccess(
    { role, accessibleRegions },
    { capability: 'referrals:void' },
  );

  return (
    <Card
      variant="outlined"
      data-testid={`ref-band-${band.market}`}
      sx={{ mb: 3 }}
    >
      <CardContent>
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          {MARKET_NAME[band.market]}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {band.referrerCount} referrers, {band.totalReferrals} referrals (
          {band.counts.pending} pending, {band.counts.qualified} qualified,{' '}
          {band.counts.paid} paid, {band.counts.void} void)
        </Typography>

        {band.referrers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No referrals in this market.
          </Typography>
        ) : (
          band.referrers.map((group) => (
            <ReferrerAccordion
              key={group.referrerId}
              group={group}
              now={now}
              canVoid={canVoid}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
};
